import React, { useEffect, useState } from 'react';
import { BaseQueryParams } from '../../../redux/Global.model';
import TwDataGrid, { TwTableData } from '../../../components/TwDataGrid/TwDataGrid';
import { GridColDef } from '@mui/x-data-grid-pro';

import TwPaginator from '../../../components/TwPaginator/TwPaginator';

import TwGenericMenu from '../../../components/TwGenericMenu/TwGenericMenu';
import {
  useGetOwnerOperatorsQuery,
  useUpdateOwnerOperatorMutation
} from '../../../redux/api/ownerOperators/ownerOperatorsApi';
import { OwnerOperator } from '../../../redux/api/ownerOperators/ownerOperators.model';
import StringUtils from '../../../utils/stringUtils';
import TwNameColumn from '../../../components/TwTable/TwNameColumn';
import { CompaniesEntities } from '../Companies';
import TwEditCompaniesModal from '../EditCompaniesModal/TwEditCompaniesModal';
import TwBadge from '../../../components/TwBadge/TwBadge';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';

const OwnerOperatorsColumns: GridColDef[] = [
  {
    field: 'name',
    headerName: 'Name',
    hideSortIcons: true,
    renderCell: (params): React.ReactNode => {
      const { value } = params;
      return value;
    }
  },
  {
    field: 'email',
    headerName: 'Email',
    hideSortIcons: true
  },
  {
    field: 'phone',
    headerName: 'Phone Number',
    hideSortIcons: true
  },
  {
    field: 'location',
    headerName: 'Location',
    hideSortIcons: true
  },
  {
    field: 'poc',
    headerName: 'Point of Contact',
    hideSortIcons: true
  },
  {
    field: 'pay_rate',
    headerName: 'Pay Rate',
    hideSortIcons: true
  },
  {
    field: 'status',
    headerName: 'Status',
    hideSortIcons: true,
    renderCell: (params): React.ReactNode => {
      const { value } = params;
      return value;
    }
  },
  {
    field: 'menu',
    headerName: '',
    width: 50,
    maxWidth: 50,
    resizable: false,
    hideSortIcons: true,
    renderCell: (params): React.ReactNode => {
      const { value } = params;
      return value;
    }
  }
];

const OwnerOperators = (): JSX.Element => {
  const [ownerOperatorsRows, setOwnerOperatorsRows] = useState<TwTableData[]>([]);

  const [itemsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [pageParams, setPageParams] = useState<BaseQueryParams>({
    offset: 0,
    limit: itemsPerPage
  });
  const { addNotification } = useNotifications();
  const navigate = useNavigate();
  const { data: ownerOperators = { content: [], total: 0 }, isLoading } =
    useGetOwnerOperatorsQuery(pageParams);
  const [updateOwnerOperator] = useUpdateOwnerOperatorMutation();

  /** Changes page */
  const handlePageChange = (value: number): void => {
    const newOffset = (value - 1) * itemsPerPage;
    setPage(value);
    setPageParams({ ...pageParams, offset: newOffset, limit: itemsPerPage });
  };

  const handleStatusChange = (ownerOperator: OwnerOperator): void => {
    updateOwnerOperator({
      id: ownerOperator.id,
      name: ownerOperator.name,
      default_pay_rate_type: ownerOperator.default_pay_rate_type,
      pay_rate: ownerOperator.pay_rate,
      deduct_fuel_purchase_on_settlement: ownerOperator.deduct_fuel_purchase_on_settlement,
      status: ownerOperator.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE'
    })
      .unwrap()
      .then((response) => {
        addNotification({
          message: 'Owner operator status updated successfully',
          variant: 'success'
        });
      })
      .catch((error) => {
        addNotification({
          message:
            error?.data?.message ?? 'Error at updating owner operator status, please try again',
          variant: 'error'
        });
      });
  };

  /**
   * Transforms owner operator response to row format for table to render content
   */
  const transformOwnerOperators = (ownerOperators: OwnerOperator[]): void => {
    const transformedOwnerOperators = ownerOperators.map((ownerOperator: OwnerOperator) => ({
      id: ownerOperator.id,
      name: <TwNameColumn name={ownerOperator.name} />,
      email: ownerOperator.company.email_address,
      phone: ownerOperator.phone
        ? StringUtils.formatPhoneNumber(ownerOperator.phone ?? '', ownerOperator.phone_ext)
        : '-',
      location: ownerOperator.location ? ownerOperator.location.full_address : '-',
      poc:
        ownerOperator.poc_first_name && ownerOperator.poc_last_name
          ? ownerOperator.poc_first_name + ' ' + ownerOperator.poc_last_name
          : '-',
      pay_rate: `${ownerOperator.pay_rate}%`,
      status: (
        <TwBadge
          content={ownerOperator.status.toLocaleLowerCase()}
          sx={{
            color: '#FFF !important',
            backgroundColor: `${
              ownerOperator.status === 'ACTIVE' ? '#10B981' : '#9CA3AF'
            } !important`
          }}
        />
      ),
      menu: (
        <TwGenericMenu
          className="driver-pay__menu"
          options={[
            {
              label: 'Edit',
              action: (): void => {
                navigate(`/owner-operators/${ownerOperator.id}`);
              }
            },
            {
              label: ownerOperator.status === 'ACTIVE' ? 'Disable' : 'Enable',
              action: (): void => handleStatusChange(ownerOperator)
            }
          ]}
        />
      )
    }));

    setOwnerOperatorsRows(transformedOwnerOperators);
  };

  useEffect(() => {
    transformOwnerOperators(ownerOperators.content);
  }, [JSON.stringify(ownerOperators.content)]);

  return (
    <>
      <Box sx={{ paddingTop: '1rem' }}>
        <TwDataGrid
          columns={OwnerOperatorsColumns}
          rows={ownerOperatorsRows}
          hideFooter={true}
          hideFooterPagination={true}
          paginationMode="server"
          loading={isLoading}
          onRowClick={(params): void => {
            navigate(`/owner-operators/${params.row.id}`);
          }}
        />
        <TwPaginator
          currentPage={page}
          items={ownerOperators.total || 0}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      </Box>
    </>
  );
};

export default OwnerOperators;
